import React from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Bell, BellOff, Smartphone, AlertCircle, Settings } from 'lucide-react';
import { Button } from './ui/button';

export const NotificationSettings = ({ user }) => {
  const { isSubscribed, isSupported, isLoading, error, permissionDenied, subscribe, unsubscribe } = usePushNotifications(user);

  const handleToggle = async () => {
    console.log('NotificationSettings: Toggle button clicked');
    console.log('Current state - isSubscribed:', isSubscribed, 'isLoading:', isLoading);
    
    try {
      if (isSubscribed) {
        console.log('Unsubscribing...');
        await unsubscribe();
      } else {
        console.log('Subscribing...');
        await subscribe();
      }
    } catch (err) {
      console.error('Error in handleToggle:', err);
    }
  };

  if (!isSupported) {
    return (
      <div className="glass-card p-6">
        <div className="flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Push Notifications Not Supported</h3>
            <p className="text-slate-300 text-sm">
              Your browser doesn't support push notifications. Please use a modern browser like Chrome, Firefox, or Safari.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <Bell className="w-6 h-6 text-green-500" />
          ) : (
            <BellOff className="w-6 h-6 text-slate-400" />
          )}
          <div>
            <h3 className="text-lg font-semibold text-white">Push Notifications</h3>
            <p className="text-sm text-slate-400">
              {isSubscribed ? 'You will receive notifications' : 'Enable to receive updates'}
            </p>
          </div>
        </div>
        <Button
          onClick={handleToggle}
          disabled={isLoading || permissionDenied}
          className={`${
            isSubscribed
              ? 'bg-red-600 hover:bg-red-700'
              : permissionDenied
              ? 'bg-slate-600 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {isLoading ? 'Loading...' : isSubscribed ? 'Disable' : permissionDenied ? 'Blocked' : 'Enable'}
        </Button>
      </div>

      {/* Permission Denied Alert */}
      {permissionDenied && (
        <div className="mt-4 p-4 bg-red-900/40 border-2 border-red-600 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-200 font-semibold mb-3">🚫 Notifications are Blocked</p>
              <p className="text-red-100 text-sm mb-3">
                You need to manually enable notifications in your device settings:
              </p>
              
              {/* Browser-specific instructions */}
              <div className="space-y-3 text-sm">
                <div className="bg-slate-900/50 p-3 rounded border border-red-800">
                  <p className="text-red-200 font-semibold mb-2">📱 On Mobile (iOS/Android):</p>
                  <ol className="list-decimal ml-4 space-y-1 text-red-100">
                    <li>Go to your device Settings</li>
                    <li>Find this app or Safari/Chrome</li>
                    <li>Tap Notifications</li>
                    <li>Enable "Allow Notifications"</li>
                    <li>Return here and tap Enable again</li>
                  </ol>
                </div>
                
                <div className="bg-slate-900/50 p-3 rounded border border-red-800">
                  <p className="text-red-200 font-semibold mb-2">💻 On Desktop:</p>
                  <ol className="list-decimal ml-4 space-y-1 text-red-100">
                    <li>Click the 🔒 lock icon in the address bar</li>
                    <li>Find "Notifications" in the permissions list</li>
                    <li>Change from "Block" to "Allow"</li>
                    <li>Refresh this page</li>
                  </ol>
                </div>
              </div>
              
              <div className="mt-3 p-2 bg-blue-900/30 border border-blue-700 rounded">
                <p className="text-blue-200 text-xs">
                  💡 <strong>Tip:</strong> You may need to close and reopen the app after changing settings
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* General Error */}
      {error && !permissionDenied && (
        <div className="mt-4 p-3 bg-red-900/30 border border-red-700 rounded-lg">
          <p className="text-sm text-red-200">{error}</p>
        </div>
      )}

      {/* iOS Instructions */}
      {!isSubscribed && !error && !permissionDenied && (
        <div className="mt-4 p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
          <div className="flex items-start gap-3">
            <Smartphone className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-200 mb-2">
                <strong>For iOS users:</strong> Add this app to your home screen first:
              </p>
              <ol className="text-sm text-blue-200 space-y-1 ml-4 list-decimal">
                <li>Tap the Share button</li>
                <li>Select "Add to Home Screen"</li>
                <li>Open the app from your home screen</li>
                <li>Enable notifications</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Subscribed Features */}
      {isSubscribed && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-300">New Projects</span>
            <span className="text-green-400">✓ Enabled</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-300">Photo Uploads</span>
            <span className="text-green-400">✓ Enabled</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-300">Admin Notes</span>
            <span className="text-green-400">✓ Enabled</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-300">Time Clock Reminders</span>
            <span className="text-green-400">✓ Enabled</span>
          </div>
        </div>
      )}
    </div>
  );
};
